import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Maximize2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { useUnlock } from "@/pages/Dashboard";
import { trackEvent } from "@/lib/api";
import { usePrice } from "@/lib/price";

const WHOP_FALLBACK = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

/**
 * Embeddable tool catalog. Each entry knows:
 *  - the public route on jobhacki.com (passed `?jhembed=1` so the backend
 *    renders its chrome-less embed variant)
 *  - the human label / blurb / tier
 *
 * `slug` is the path-segment used at /dashboard/embed/<slug>.
 */
export const EMBED_TOOLS = {
  resume: {
    label: "Resume Builder",
    blurb: "Rebuild your resume for a specific role — ATS-tuned, downloadable.",
    route: "/resume",
    tier: "paid",
  },
  simulator: {
    label: "Readiness Simulator",
    blurb: "Voice + text role-play, scored against a real posting.",
    route: "/simulator",
    tier: "paid",
  },
  rolefit: {
    label: "RoleFit Test",
    blurb: "Take the assessment for this role — score syncs back to your dashboard.",
    route: "/rolefit",
    tier: "free",
  },
};

export default function ToolEmbed() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const ctx = useUnlock();
  const tool = EMBED_TOOLS[slug];
  const iframeRef = React.useRef(null);
  const { price, checkout } = usePrice();
  const whop = checkout || WHOP_FALLBACK;

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const goFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) {
      try {
        req.call(el);
      } catch {
        /* ignore */
      }
    }
  };

  // Block paid tools for free users — bounce to the upsell modal.
  useEffect(() => {
    if (tool && tool.tier === "paid" && ctx && !ctx.paid) {
      ctx.open(tool.label);
      nav("/dashboard", { replace: true });
    }
  }, [tool, ctx, nav]);

  // Fire the per-tool start event once the iframe is about to mount.
  // - /simulator → simulator.start (with `role`)
  // - /rolefit   → rolefit.start (with `role`)
  // - /resume    → resume.build
  useEffect(() => {
    if (!tool) return;
    if (slug === "simulator") {
      const role = searchParams.get("role") || null;
      trackEvent("simulator.start", { role });
    } else if (slug === "rolefit") {
      const role = searchParams.get("role") || null;
      trackEvent("rolefit.start", { role });
    } else if (slug === "resume") {
      trackEvent("resume.build");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tool]);

  // 7-second timeout — if the iframe didn't fire onLoad we show a soft error.
  useEffect(() => {
    if (loaded || errored || !tool) return undefined;
    const t = setTimeout(() => {
      if (!loaded) setErrored(true);
    }, 7000);
    return () => clearTimeout(t);
  }, [loaded, errored, tool]);

  if (!tool) {
    return (
      <div
        data-testid="embed-not-found"
        className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-8 max-w-xl"
      >
        <div className="font-display text-2xl">Unknown tool.</div>
        <p className="font-mono-body text-sm mt-2 text-[#0a0a0a]/70">
          That tool doesn&apos;t exist in the embed catalog.
        </p>
        <Link
          to="/dashboard"
          className="hover-pop mt-5 inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-xs"
        >
          <ArrowLeft size={12} strokeWidth={3} /> Back to dashboard
        </Link>
      </div>
    );
  }

  // Build iframe src: forward any incoming query params (e.g. ?role=sdr) and
  // tack on jhembed=1 so the public route renders its chrome-less variant.
  //
  // Simulator + RoleFit need `role` to render anything useful — if the caller
  // didn't pass one, fall back to the user's last selected role (saved in
  // localStorage by RoleFit.jsx) and then to a neutral "general" role so the
  // iframe never loads a blank stub.
  const fwd = new URLSearchParams(searchParams);
  if ((slug === "simulator" || slug === "rolefit") && !fwd.get("role")) {
    let lastRole = null;
    try {
      lastRole = window.localStorage.getItem("jh_last_role_v1");
    } catch {
      /* ignore */
    }
    fwd.set("role", lastRole || "general");
  }
  fwd.set("jhembed", "1");
  const src = `https://jobhacki.com${tool.route}?${fwd.toString()}`;

  // Public (non-embed) URL — same query params, just no jhembed=1 — for the
  // "open in new tab" fallback links.
  const publicQs = new URLSearchParams(searchParams).toString();
  const publicHref = `https://jobhacki.com${tool.route}${publicQs ? `?${publicQs}` : ""}`;

  return (
    <div
      data-testid={`embed-${slug}`}
      className="flex flex-col h-[calc(100vh-150px)] min-h-[520px]"
    >
      {/* Tool topbar */}
      <header className="bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] border-b-0 px-4 py-2.5 flex items-center gap-3">
        <Link
          to="/dashboard"
          data-testid="embed-back"
          className="hover-pop inline-flex items-center gap-1.5 bg-[#FFC400] text-[#0a0a0a] border-2 border-[#FFC400] px-2.5 py-1 font-accent text-[11px]"
        >
          <ArrowLeft size={12} strokeWidth={3} /> Dashboard
        </Link>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm md:text-base truncate">
            {tool.label}
          </div>
          <div className="font-mono-body text-[10px] text-[#FDFBF7]/55 truncate">
            {tool.blurb}
          </div>
        </div>
        <button
          type="button"
          onClick={goFullscreen}
          data-testid="embed-fullscreen"
          aria-label="Fullscreen"
          title="Fullscreen"
          className="hover-pop bg-[#FDFBF7]/10 hover:bg-[#FFC400] hover:text-[#0a0a0a] border-2 border-[#FFC400]/40 hover:border-[#FFC400] w-8 h-8 inline-flex items-center justify-center"
        >
          <Maximize2 size={13} strokeWidth={3} />
        </button>
        <a
          href={publicHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="embed-new-tab"
          aria-label="Open in new tab"
          className="hover-pop bg-[#FDFBF7]/10 hover:bg-[#FFC400] hover:text-[#0a0a0a] border-2 border-[#FFC400]/40 hover:border-[#FFC400] w-8 h-8 inline-flex items-center justify-center"
        >
          <ExternalLink size={13} strokeWidth={3} />
        </a>
      </header>

      {/* The iframe itself */}
      <div className="relative flex-1 border-4 border-[#0a0a0a] border-t-0 bg-white overflow-hidden">
        {!loaded && !errored && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#FDFBF7]">
            <div className="flex items-center gap-3 font-mono-body text-sm text-[#0a0a0a]/65">
              <Loader2 size={16} className="animate-spin" /> Loading {tool.label}…
            </div>
          </div>
        )}
        {errored && (
          <div className="absolute inset-0 flex items-center justify-center p-6 bg-[#FDFBF7]">
            <div className="max-w-md text-center">
              <AlertTriangle
                size={28}
                strokeWidth={2.5}
                className="text-[#E10600] mx-auto mb-3"
              />
              <div className="font-display text-2xl mb-2">
                Couldn&apos;t embed {tool.label}.
              </div>
              <p className="font-mono-body text-sm text-[#0a0a0a]/70 mb-5">
                The backend may not yet allow framing for this tool. Open it in a new tab to use the full version.
              </p>
              <a
                href={publicHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="embed-fallback-newtab"
                className="hover-pop inline-flex items-center gap-2 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm"
              >
                Open in new tab <ExternalLink size={12} strokeWidth={3} />
              </a>
              {tool.tier === "paid" && ctx && !ctx.paid && (
                <a
                  href={whop}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-pop ml-2 inline-flex items-center gap-2 bg-[#FFC400] text-[#0a0a0a] border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm"
                >
                  Unlock {price}
                </a>
              )}
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          key={src}
          src={src}
          title={tool.label}
          data-testid="embed-iframe"
          allowFullScreen
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          allow="clipboard-write; clipboard-read; microphone; camera; fullscreen"
          className="w-full h-full block bg-white"
        />
      </div>
    </div>
  );
}
